// Client-intent order events, Measurement Core v1.3.12 §6.5:
//
//   order_started    the ticket is activated for an instrument
//   order_submitted  the order request is sent
//
// Both carry asset_type, order_type and record_context, the same three
// classifications the backend puts on order_accepted / order_rejected, worked
// out the same way: asset_type from the ticker exactly as the order request
// sends it, order_type from the ticket tab, record_context from the league the
// ticket trades in. No ticker, quantity, price or value is ever included.
//
// order_submitted also creates the order's client_order_id, which goes to the
// backend with the request (it is saved with the order and its outcome) and is
// never sent to analytics (§6.5: an internal transport key).
import { trackClientEvent } from "./measurement";
import { leagueTypeKey } from "../pages/Game/landing/leagueFormat";

const ORDER_TYPES = { market: "market", limit: "limit", stopLoss: "stop" };
const RECORD_CONTEXTS = { cash: "cash_game", private: "private_league", public: "public_league" };

const assetType = (ticker) => {
  if (!ticker) return undefined;
  if (ticker.startsWith("C:")) return "forex";
  if (ticker.startsWith("X:")) return "crypto";
  return "stock";
};

// A ticket that knows it trades in a league (gameId) but not which kind leaves
// record_context out rather than guessing.
export const orderMeasurementParams = ({ ticker, orderTab, game, gameId }) => ({
  asset_type: assetType(ticker),
  order_type: ORDER_TYPES[orderTab],
  record_context: game
    ? RECORD_CONTEXTS[leagueTypeKey(game)]
    : gameId
    ? undefined
    : "main_record",
});

export const trackOrderStarted = (ticket) => {
  trackClientEvent("order_started", orderMeasurementParams(ticket));
};

/** Logs order_submitted and returns the client_order_id to send with the order. */
export const trackOrderSubmitted = (ticket) => {
  const clientOrderId = newClientOrderId();
  trackClientEvent("order_submitted", orderMeasurementParams(ticket));
  return clientOrderId;
};

const newClientOrderId = () => {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch (e) { }
  return Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 12);
};

// A placed order the backend reports as already filled (a market order that
// executed at placement). ORDER_STATUS FILLED is 3.
export const isConfirmedFill = (responseData) =>
  String(responseData?.data?.order_status) === "3";

/**
 * trade_confirmation_viewed (Core §6.5, "Client shows confirmed fill"): call it
 * once the "Order filled." message is on screen. asset_type and record_context
 * come from the backend's own trade_filled for this order; the ticket is only a
 * fallback for a response that carried no measurement events.
 */
export const trackTradeConfirmationViewed = (responseData, ticket) => {
  const filled = (responseData?.measurement_events || []).find((e) => e?.name === "trade_filled");
  const params = filled?.params || orderMeasurementParams(ticket);
  trackClientEvent("trade_confirmation_viewed", {
    asset_type: params.asset_type,
    record_context: params.record_context,
  });
};
